import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  Typography,
  Box,
  Alert,
  TextField
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { previewPlanChange } from '../services/paymentService';
import ProrationConfirmationDialog from './ProrationConfirmationDialog';

// Helper function to calculate pricing based on new tiered model
const calculateMonthlyAmount = (roomCount) => {
  if (roomCount <= 3) {
    return 9.00;
  } else {
    const additionalRooms = roomCount - 3;
    return 9.00 + (additionalRooms * 3.00);
  }
};

// Helper function to get pricing description
const getPricingDescription = (roomCount) => {
  if (roomCount <= 3) {
    return `Up to 3 rooms: $9/month`;
  } else {
    const additionalRooms = roomCount - 3;
    return `3 rooms ($9) + ${additionalRooms} additional ($${additionalRooms * 3})`;
  }
};

export default function SubscriptionPlanSelector({ 
  open, 
  onClose, 
  currentSubscription, 
  organizationId,
  onPlanUpdate 
}) {
  const [maxRooms, setMaxRooms] = useState(Math.max(currentSubscription?.maxRooms || 3, 3));
  const [updating, setUpdating] = useState(false);
  const [prorationData, setProrationData] = useState(null);
  const [prorationDialogOpen, setProrationDialogOpen] = useState(false);
  const [loadingProration, setLoadingProration] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handlePreviewPlanChange = async () => {
    if (!maxRooms || maxRooms < 1 || !currentSubscription || !organizationId || organizationId === 'undefined') {
      console.error('❌ Missing required data for plan preview:', { maxRooms, organizationId, currentSubscription: !!currentSubscription });
      return;
    }

    setLoadingProration(true);
    try {
      const response = await previewPlanChange(organizationId, maxRooms);
      setProrationData(response.prorationData);
      setProrationDialogOpen(true);
    } catch (error) {
      console.error('Error previewing plan change:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to preview plan change',
        { variant: 'error' }
      );
    } finally {
      setLoadingProration(false);
    }
  };

  const handleConfirmProration = async () => {
    if (!maxRooms || !currentSubscription) {
      return;
    }

    setUpdating(true);
    try {
      const result = await onPlanUpdate(maxRooms);
      
      // Show success message with charge details if available
      let successMessage = 'Subscription plan updated successfully!';
      if (result?.immediateCharge?.success && result?.prorationDetails) {
        successMessage += ` ${result.prorationDetails.description} charged immediately.`;
      } else if (result?.prorationDetails) {
        successMessage += ` ${result.prorationDetails.description} will be processed.`;
      }
      
      enqueueSnackbar(successMessage, { variant: 'success', autoHideDuration: 6000 });
      setProrationDialogOpen(false);
      onClose();
    } catch (error) {
      console.error('Error updating plan:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to update subscription plan',
        { variant: 'error' }
      );
    } finally {
      setUpdating(false);
    }
  };

  const currentRooms = currentSubscription?.roomsCount || 0;
  const currentMaxRooms = currentSubscription?.maxRooms || 0;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h5">Update Your Subscription</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Change the number of rooms for your organization
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {currentRooms > 3 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You currently have {currentRooms} room{currentRooms !== 1 ? 's' : ''} created. 
            You can only choose a number that supports your current rooms (minimum {currentRooms}).
          </Alert>
        )}
        
        {currentRooms <= 3 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Our base plan includes up to 3 rooms for $9/month. Additional rooms are $3 each.
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <TextField
            label="Number of Rooms"
            type="number"
            fullWidth
            required
            value={maxRooms}
            onChange={(e) => setMaxRooms(parseInt(e.target.value) || Math.max(currentRooms, 3))}
            inputProps={{ min: Math.max(currentRooms, 3), max: 1000 }}
            sx={{ mb: 3 }}
            disabled={updating}
            helperText={`Minimum ${Math.max(currentRooms, 3)} room${Math.max(currentRooms, 3) !== 1 ? 's' : ''} (${currentRooms > 3 ? 'your current usage' : 'base plan'}), maximum 1000 rooms`}
            error={maxRooms < Math.max(currentRooms, 3)}
          />

          {/* Pricing Display */}
          <Card variant="outlined" sx={{ p: 3, bgcolor: 'primary.50' }}>
            <Box textAlign="center">
              <Typography variant="h4" color="primary" gutterBottom>
                ${calculateMonthlyAmount(maxRooms)}
                <Typography variant="body2" component="span" color="text.secondary">
                  /month
                </Typography>
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 1 }}>
                {maxRooms} Room{maxRooms !== 1 ? 's' : ''}
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                {getPricingDescription(maxRooms)}
              </Typography>
            </Box>
          </Card>
        </Box>

        {/* Change Summary */}
        {maxRooms !== currentMaxRooms && (
          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Plan Change Summary
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Current: {currentMaxRooms} rooms - ${currentSubscription?.monthlyAmount || 0}/month
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              New: {maxRooms} rooms - ${calculateMonthlyAmount(maxRooms)}/month
            </Typography>
            <Typography variant="body2" color="primary" fontWeight="bold">
              Change: ${calculateMonthlyAmount(maxRooms) - (currentSubscription?.monthlyAmount || 0)}/month
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={updating || loadingProration}>
          Cancel
        </Button>
        <Button 
          onClick={handlePreviewPlanChange}
          variant="contained"
          disabled={!maxRooms || maxRooms < Math.max(currentRooms, 3) || maxRooms === currentMaxRooms || updating || loadingProration}
        >
          {loadingProration ? 'Loading...' : 'Preview Changes'}
        </Button>
      </DialogActions>
    </Dialog>

    <ProrationConfirmationDialog
      open={prorationDialogOpen}
      onClose={() => setProrationDialogOpen(false)}
      prorationData={prorationData}
      onConfirm={handleConfirmProration}
      loading={updating}
    />
  </>
  );
} 